import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { API } from "../../shared/apiRoutes";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { getAllProductSettingsResponse, getPreOrderQuantityResponse, InventoryProductsPayload, InventoryQuantityResponse, PreOrderQuantityPayload, ProductListResponse, InventoryQuantityPayload, SetProductQuantityResponse } from "../../components/products/inventory/inventory.model";


interface initialData {
    getProductSettings: { loading: boolean, error: string, value: null | getAllProductSettingsResponse },
    getAllInventoryProducts: { loading: boolean, error: string, value: null | ProductListResponse },
    getPreOrderQty: { loading: boolean, error: string, value: null | getPreOrderQuantityResponse },
    addQuantity: { loading: boolean, error: string, value: null | InventoryQuantityResponse },
    setQuantity: { loading: boolean, error: string, value: null | SetProductQuantityResponse },
    inventoryCache: Record<string, ProductListResponse>,
}

const initialState: initialData = {
    getProductSettings: { loading: true, error: '', value: null },
    getAllInventoryProducts: { loading: true, error: '', value: null },
    getPreOrderQty: { loading: true, error: '', value: null },
    addQuantity: { loading: true, error: '', value: null },
    setQuantity: { loading: true, error: '', value: null },
    inventoryCache: {},
}

export const getAllPosProductSettings = createAsyncThunk(
    "inventory/getAllPosProductSettings",
    async (storeId: number, { getState }) => {
        const state = getState() as any;
        if (state.inventory?.getProductSettings?.value) {
            return state.inventory.getProductSettings.value;
        }
        const url = HttpRoutingService.replaceUrl(API.getProductSettings, { storeId: storeId });
        return HttpRoutingService.getMethod<getAllProductSettingsResponse>(url, '', 'v1/');
    }
)

export const getAllPosInventoryProducts = createAsyncThunk(
    "inventory/getAllPosInventoryProducts",
    async (
        { storeId, data, refresh }: { storeId: number, data: InventoryProductsPayload, refresh?: boolean },
        { getState }
    ) => {
        const cacheKey = JSON.stringify({ storeId, data: { ...(data as any), refresh: undefined } });
        const state = getState() as any;
        const bypassCache = refresh || (data as any)?.refresh;
        if (!bypassCache && state.inventory?.inventoryCache?.[cacheKey]) {
            return state.inventory.inventoryCache[cacheKey];
        }
        const url = HttpRoutingService.replaceUrl(API.getAllInventoryProducts, { storeId: storeId });
        return HttpRoutingService.getMethod<ProductListResponse>(url, data);
    }
)

export const getPosPreOrderQuantity = createAsyncThunk("inventory/getPosPreOrderQuantity", ({ storeId, data }: PreOrderQuantityPayload) => {
    const url = HttpRoutingService.replaceUrl(API.getAllPreOrderQty, { storeId: storeId });
    return HttpRoutingService.getMethod<getPreOrderQuantityResponse>(url, data, 'v1/');
})

export const addPosInventoryQuantity = createAsyncThunk("inventory/addPosInventoryQuantity", ({ storeId, data }: InventoryQuantityPayload) => {
    const url = HttpRoutingService.replaceUrl(API.addQuantity, { storeId: storeId });
    return HttpRoutingService.putMethod<InventoryQuantityResponse>(url, data);
})

export const setPosInventoryQuantity = createAsyncThunk("inventory/setPosInventoryQuantity", ({ storeId, data }: InventoryQuantityPayload) => {
    const url = HttpRoutingService.replaceUrl(API.setQuantity, { storeId: storeId });
    return HttpRoutingService.putMethod<SetProductQuantityResponse>(url, data);
})

export const InventorySlice = createSlice({
    name: "inventory",
    reducers: {
        clearInventoryCache: (state) => {
            state.inventoryCache = {};
        }
    },
    initialState: initialState,

    extraReducers: (builder) => {
        builder.addCase(getAllPosProductSettings?.pending, (state) => {
            state.getProductSettings.loading = true
        })
        builder.addCase(getAllPosProductSettings?.fulfilled, (state, action: PayloadAction<getAllProductSettingsResponse>) => {
            state.getProductSettings.loading = false
            state.getProductSettings.value = action?.payload
        })
        builder.addCase(getAllPosProductSettings?.rejected, (state, action) => {
            state.getProductSettings.loading = false
            state.getProductSettings.error = action?.error?.message ? action?.error?.message : 'Error in getProductSettings'
        })
        builder.addCase(getAllPosInventoryProducts?.pending, (state) => {
            state.getAllInventoryProducts.loading = true
        })
        builder.addCase(getAllPosInventoryProducts?.fulfilled, (state, action: any) => {
            state.getAllInventoryProducts.loading = false
            state.getAllInventoryProducts.value = action?.payload
            const cacheKey = JSON.stringify({
                storeId: action.meta.arg.storeId,
                data: { ...(action.meta.arg.data as any), refresh: undefined }
            });
            if (!state.inventoryCache) {
                state.inventoryCache = {};
            }
            state.inventoryCache[cacheKey] = action.payload;
        })
        builder.addCase(getAllPosInventoryProducts?.rejected, (state, action) => {
            state.getAllInventoryProducts.loading = false
            state.getAllInventoryProducts.error = action?.error?.message ? action?.error?.message : 'Error in getAllInventoryProducts'
        })
        builder.addCase(getPosPreOrderQuantity?.pending, (state) => {
            state.getPreOrderQty.loading = true
        })
        builder.addCase(getPosPreOrderQuantity?.fulfilled, (state, action: PayloadAction<getPreOrderQuantityResponse>) => {
            state.getPreOrderQty.loading = false
            state.getPreOrderQty.value = action?.payload
        })
        builder.addCase(getPosPreOrderQuantity?.rejected, (state, action) => {
            state.getPreOrderQty.loading = false
            state.getPreOrderQty.error = action?.error?.message ? action?.error?.message : 'Error in getPreOrderQty'
        })
        builder.addCase(addPosInventoryQuantity?.pending, (state) => {
            state.addQuantity.loading = true
        })
        builder.addCase(addPosInventoryQuantity?.fulfilled, (state, action: PayloadAction<InventoryQuantityResponse>) => {
            state.addQuantity.loading = false
            state.addQuantity.value = action?.payload
            state.inventoryCache = {};
        })
        builder.addCase(addPosInventoryQuantity?.rejected, (state, action) => {
            state.addQuantity.loading = false
            state.addQuantity.error = action?.error?.message ? action?.error?.message : 'Error in addQuantity'
        })
        builder.addCase(setPosInventoryQuantity?.pending, (state) => {
            state.setQuantity.loading = true
        })
        builder.addCase(setPosInventoryQuantity?.fulfilled, (state, action: PayloadAction<SetProductQuantityResponse>) => {
            state.setQuantity.loading = false
            state.setQuantity.value = action?.payload
            state.inventoryCache = {};
        })
        builder.addCase(setPosInventoryQuantity?.rejected, (state, action) => {
            state.setQuantity.loading = false
            state.setQuantity.error = action?.error?.message ? action?.error?.message : 'Error in setQuantity'
        })
    }
})

export const { clearInventoryCache } = InventorySlice.actions;

export default InventorySlice?.reducer